import type { AxiosProgressEvent } from "axios";
import { handleErrorAndRethrow, instance } from "./axios-service";
import { RefEntityType, VaultFileType } from './../types/enum-type';
import { VaultFileTag } from "@/types/enum-type";

export async function doUploadToVault(
    file: File,
    url: string,
    params: {
        refEntityType: string;
        refEntityId: string;
        type: string;
        tag?: string;
        replaceExisting?: boolean;

    },
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("refEntityType", params.refEntityType);
    formData.append("refEntityId", params.refEntityId.toString());
    formData.append("type", params.type);
    formData.append("replaceExisting", params.replaceExisting ? "true" : "false");

    if (params.tag) {
        formData.append("tag", params.tag);
    }

    try {
        return await instance.post(url, formData, {
            onUploadProgress,
        });
    } catch (e) {
        handleErrorAndRethrow(e);
    }

}

export function uploadTaskSignature(dataUrl: string, taskId: string, onUploadProgress) {
    // dataURL → Blob
    const blob = dataURLToBlob(dataUrl);

    // Blob → File
    const file = new File([blob], "signature.png", {
        type: "image/png",
    });

    return doUploadToVault(file, "/vault-files", {
        refEntityType: RefEntityType.TASK,
        refEntityId: taskId,
        type: VaultFileType.SIGNATURE
    }, onUploadProgress);
}

export function uploadPannePhoto(
    dataUrl: string,
    panneId: string,
    onUploadProgress,
    tag?: VaultFileTag,
) {
    // dataURL → Blob
    const blob = dataURLToBlob(dataUrl);

    // Blob → File
    const file = new File([blob], "photo.png", {
        type: "image/png",
    });

    return doUploadToVault(file, "/vault-files", {
        refEntityType: RefEntityType.PANNE,
        refEntityId: panneId,
        type: VaultFileType.PHOTO,
        tag,
        replaceExisting: true
    }, onUploadProgress);
}

export async function fetchVaultFiles(params: {
    refEntityType: string;
    refEntityId: string;
    type: string;
    tag?: string;
}) {
    const { refEntityType, refEntityId, type, tag } = params;

    const queryParams = new URLSearchParams({
        ref_entity_type: refEntityType,
        ref_entity_id: refEntityId,
        type
    });

    if (tag) {
        queryParams.append("tag", tag);
    }

    const response = await instance.get(`/vault-files?${queryParams.toString()}`);
    return response.data;
}

function dataURLToBlob(dataURL: string): Blob {
    const [header, base64] = dataURL.split(",");
    const mime = header.match(/:(.*?);/)?.[1] ?? "image/png";

    const binary = atob(base64);
    const array = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        array[i] = binary.charCodeAt(i);
    }

    return new Blob([array], { type: mime });
}

export async function fetchFileContent(fileId: string) : Promise<string> {
  const response = await instance.get(`/vault-files/${fileId}/content`, {
    responseType: "blob",
  });
  const blob = new Blob([response.data], { type: response.headers["content-type"] });
  return URL.createObjectURL(blob);
}

export async function fetchThumbnail(fileId: string) : Promise<string> {
  const response = await instance.get(`/vault-files/${fileId}/thumbnail`, {
    responseType: "blob",
  });
  const blob = new Blob([response.data], { type: response.headers["content-type"] });
  return URL.createObjectURL(blob);
}
